import { useReportingStore } from "../../../../../stores/ReportingStore";
import LoaderStats from "../../LoaderStats";
import {
  ActionIcon,
  Checkbox,
  ColorSwatch,
  Flex,
  Group,
  Menu,
  Paper,
  RingProgress,
  Stack,
  Text,
} from "@mantine/core";
import { LineChart } from "@mantine/charts";
import { IconCheck, IconDots } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import {
  getChartColors,
  getReportingRateColor,
} from "../../../../../functions/utilFunctions";
import { useLanguage } from "../../../../../stores/LanguageStore";

function getTotals(stats) {
  const totals = stats.reduce((acc, curr) => {
    Object.keys(curr).forEach((key) => {
      if (key !== "date") {
        acc[key] = (acc[key] || 0) + curr[key];
      }
    });
    return acc;
  }, {});

  return {
    opener_total: totals.opener,
    clicker_total: totals.clicker,
    answerer_total: totals.answerer,
    contact_email_bounce: totals.bounce,
    ...totals,
  };
}

const PerfRateMulti = () => {
  const lang = useLanguage((s) => s.language);
  const [isGlobal, setIsGlobal] = useState(true);
  const progStats = useReportingStore((s) => s.progStats);
  const [noCLicker, setNoClicker] = useState(false);

  const openersRate = useMemo(() => {
    let result = {};
    progStats.forEach((item) => {
      let statistics = getTotals(item.stats);

      if (statistics.sent) {
        result[item.prog.id] =
          (statistics.opener_total / statistics.sent) * 100;
      } else {
        result[item.prog.id] = 0;
      }
    });

    return result;
  }, [progStats]);

  const clickersRate = useMemo(() => {
    let result = {};
    progStats.forEach((item) => {
      let statistics = getTotals(item.stats);

      if (isGlobal) {
        if (statistics.opener_total) {
          result[item.prog.id] =
            (statistics.clicker_total / statistics.opener_total) * 100;
        } else {
          result[item.prog.id] = 0;
        }
      } else {
        if (statistics.sent) {
          result[item.prog.id] =
            (statistics.clicker_total / statistics.sent) * 100;
        } else {
          result[item.prog.id] = 0;
        }
      }
    });

    return result;
  }, [isGlobal, progStats]);

  const answerersRate = useMemo(() => {
    let result = {};
    progStats.forEach((item) => {
      let statistics = getTotals(item.stats);

      if (isGlobal) {
        if (statistics.opener_total) {
          result[item.prog.id] =
            (statistics.answerer_total / statistics.opener_total) * 100;
        } else {
          result[item.prog.id] = 0;
        }
      } else {
        if (statistics.sent) {
          result[item.prog.id] =
            (statistics.answerer_total / statistics.sent) * 100;
        } else {
          result[item.prog.id] = 0;
        }
      }
    });

    return result;
  }, [isGlobal, progStats]);

  const data = useMemo(() => {
    let opener = {
      type: lang.statistics.contacts_type.opener,
    };
    let clicker = {
      type: lang.statistics.contacts_type.clicker,
    };
    let answerer = {
      type: lang.statistics.contacts_type.answerer,
    };

    Object.keys(openersRate).forEach((key) => {
      opener[`rate_${key}`] = Math.round(openersRate[key] * 10) / 10;
    });
    Object.keys(clickersRate).forEach((key) => {
      clicker[`rate_${key}`] = Math.round(clickersRate[key] * 10) / 10;
    });
    Object.keys(answerersRate).forEach((key) => {
      answerer[`rate_${key}`] = Math.round(answerersRate[key] * 10) / 10;
    });

    return [opener, clicker, answerer];
  }, [answerersRate, clickersRate, openersRate, lang]);

  const series = useMemo(() => {
    return progStats.map((item, index) => ({
      name: `rate_${item.prog.id}`,
      label: `${item.prog.workflow_name}`,
      color: `${getChartColors()[index]}.5`,
    }));
  }, [progStats]);

  const openPerf = useMemo(() => (noCLicker ? 0.3 : 0.25), [noCLicker]);
  const clickPerf = 0.35;
  const answerPerf = useMemo(() => (noCLicker ? 0.7 : 0.4), [noCLicker]);

  return (
    <Paper pos={"relative"} flex={1} p={"xl"} pt={"md"} pb={"md"}>
      <LoaderStats isProgStats />

      <Group mb={32} justify="center">
        <Text size="lg" fw={600}>
          {lang.reports.perf_title}
        </Text>

        <Menu>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              size={"xl"}
              pos={"absolute"}
              top={8}
              right={8}
            >
              <IconDots size={32} />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item onClick={() => setIsGlobal(!isGlobal)}>
              <Group justify="end" gap={"xs"}>
                {isGlobal && <IconCheck />}
                <Text size="md">{lang.reports.base_engaged}</Text>
              </Group>
            </Menu.Item>
            <Menu.Item onClick={() => setIsGlobal(!isGlobal)}>
              <Group justify="end" gap={"xs"}>
                {!isGlobal && <IconCheck />}
                <Text size="md">{lang.reports.base_sent}</Text>
              </Group>
            </Menu.Item>

            <Menu.Divider mb={8} />

            <Checkbox
              ml={12}
              my={4}
              checked={!noCLicker}
              onClick={() => setNoClicker(!noCLicker)}
              label={lang.statistics.contacts_type.clicker}
              size="md"
            />
          </Menu.Dropdown>
        </Menu>
      </Group>

      <LineChart
        h={200}
        w={"100%"}
        data={data}
        dataKey="type"
        series={series}
        tickLine="y"
        unit="%"
        dotProps={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
        activeDotProps={{ r: 4, strokeWidth: 1, fill: "#fff" }}
        xAxisProps={{ padding: { left: 64, right: 64 } }}
        lineProps={{ isAnimationActive: true, animationDuration: 600 }}
      />

      <Group mt={24} justify="space-between">
        <Stack gap={"xs"} w={"100%"}>
          {progStats.map((item, index) => {
            let itemOpenersRate = openersRate[item.prog.id];
            let itemClickersRate = clickersRate[item.prog.id];
            let itemAnswerersRate = answerersRate[item.prog.id];
            let perfRate =
              (itemOpenersRate * openPerf +
                itemClickersRate * clickPerf +
                itemAnswerersRate * answerPerf) /
              (openPerf + clickPerf + answerPerf);

            return (
              <Group key={item.prog.id} w={"100%"}>
                <ColorSwatch
                  color={`var(--mantine-color-${getChartColors()[index]}-5)`}
                  size={16}
                />
                <Text size="sm">
                  {item.prog.workflow_name} #{item.prog.id}
                </Text>

                <RingProgress
                  ml={"auto"}
                  label={
                    <Group justify="center" ml={6} mt={-4}>
                      <Text
                        c={getReportingRateColor(perfRate)}
                        fz={14}
                        ta={"center"}
                        fw={600}
                      >
                        {Math.floor(perfRate)}
                      </Text>
                    </Group>
                  }
                  size={48}
                  roundCaps={perfRate < 92}
                  thickness={4}
                  sections={[
                    { value: 100, color: getReportingRateColor(perfRate) },
                  ]}
                />
              </Group>
            );
          })}
        </Stack>
      </Group>
    </Paper>
  );
};

export default PerfRateMulti;
